import { useTransientNavigationState } from '../navigation/useTransientNavigationState';
import { useNavigationState } from '../navigation/NavigationProvider';
import { NavigationCount } from '../../components/ui/navigation';
import { DeleteIcon, EditIcon, FilterIcon } from '../../components/ui/icons';
import { DialogContent } from '../../components/ui/DialogContent';
import { recordKey } from '../collection/multiCollectionIdentity';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, List, TextField, Typography } from '@mui/material';
import { Item } from '../../domain/item';
import { CollectionPage } from '../collection/components/CollectionPage';
import { CollectionHeader } from '../collection/components/CollectionHeader';
import { CollectionViewControl } from '../collection/components/CollectionViewControl';
import { CollectionSortControl } from '../collection/components/CollectionSortControl';
import { CollectionSearchField } from '../collection/components/CollectionSearchField';
import { CollectionEmptyState } from '../collection/components/CollectionEmptyState';
import { useCollectionSelection } from '../collection/hooks/useCollectionSelection';
import {
  CollectionSortOption,
  CollectionView,
  LocationSort,
} from '../collection/types';
import { DialogActions } from '../../components/ui/DialogActions';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { LocationEntry } from './LocationEntry';
import { Location } from '../../domain/location';
import {
  LocationEditorDialog,
  LocationUpdate,
} from './components/LocationEditorDialog';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { CardSize, DISPLAY_SIZE_PROFILES } from '../../hooks/useUiPreferences';

const locationSortOptions: CollectionSortOption<LocationSort>[] = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'edited-desc', label: 'Recently edited' },
  { value: 'created-desc', label: 'Recently added' },
  { value: 'items-desc', label: 'Most items' },
  { value: 'items-asc', label: 'Fewest items' },
];
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export interface LocationsController {
  items: Item[];
  locations: Location[];
  update: (
    updates: { id: string; name?: string; description?: string }[],
  ) => void;
  delete: (locationIds: string[]) => void;
  saveLocation: (update: LocationUpdate) => Promise<boolean>;
  deleteLocation: (location: Location) => Promise<boolean>;
}

export function LocationsPage({
  controller,
  onAddLocation,
  phoneLayout,
  onCountChange,
  hideCount = false,
  stickyTop = phoneLayout ? 0 : 48,
  shellHeader,
  view,
  onViewChange,
  sort,
  onSortChange,
  cardSize,
  removeMargins = false,
  readOnly = false,
  disableBulkActions = false,
  loading = false,
  error = null,
  onRefresh,
  incompleteResults = false,
  inventoryStatus,
  onOpenLocation,
  itemCounts: providedItemCounts,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
}: {
  controller: LocationsController;
  onAddLocation: () => void;
  phoneLayout: boolean;
  onCountChange?: (count: NavigationCount) => void;
  hideCount?: boolean;
  stickyTop?: number;
  shellHeader?: React.ReactNode;
  view: CollectionView;
  onViewChange: (view: CollectionView) => void;
  sort: LocationSort;
  onSortChange: (sort: LocationSort) => void;
  cardSize: CardSize;
  removeMargins?: boolean;
  readOnly?: boolean;
  disableBulkActions?: boolean;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  incompleteResults?: boolean;
  inventoryStatus?: 'loading' | 'updating';
  onOpenLocation?: (location: Location) => void;
  /** When supplied, absent IDs have unknown counts rather than zero. */
  itemCounts?: Record<string, number>;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
}) {
  const size = DISPLAY_SIZE_PROFILES[cardSize];
  const { locations, items, update, delete: deleteLocations } = controller;
  const [searchQuery, setSearchQuery] = useState('');
  const [editingIds, setEditingIds] = useState<string[]>([]);
  const editScopeKey = `locations.edit:${collectionId || 'visible'}`;
  const bulkScopeKey = `locations.bulk:${collectionId || 'visible'}`;
  const [editingLocationKey, setEditingLocationKey] = useNavigationState<
    string | null
  >(editScopeKey, null);
  const editingLocation =
    locations.find((entry) => recordKey(entry) === editingLocationKey) || null;
  const setEditingLocation = (location: Location | null) =>
    setEditingLocationKey(location ? recordKey(location) : null);
  const [editDescription, setEditDescription] = useState('');
  const [showEditDialog, setShowEditDialog] =
    useTransientNavigationState<boolean>(bulkScopeKey, false);
  const [showDeleteConfirm, setShowDeleteConfirm] =
    useTransientNavigationState<boolean>(
      `locations.delete:${collectionId || 'visible'}`,
      false,
    );
  const selection = useCollectionSelection(
    locations.map(recordKey),
    disableBulkActions,
  );
  const closeBulkEdit = () => {
    setShowEditDialog(false);
    setEditingIds([]);
  };
  const { requestClose: requestBulkEditClose, closeAfterSave: finishBulkEdit } =
    useUnsavedChangesGuard({
      scopeKey: bulkScopeKey,
      open: showEditDialog,
      dirty: editDescription !== '',
      onDiscard: () => setEditDescription(''),
      onClose: closeBulkEdit,
    });
  const closeBulkEditWithGuard = () => {
    void requestBulkEditClose();
  };

  const itemCounts = useMemo(() => {
    if (providedItemCounts) return new Map(Object.entries(providedItemCounts));
    const counts = new Map(
      locations.map((location) => [recordKey(location), 0]),
    );
    items.forEach((item) => {
      const location = locations.find(
        (entry) =>
          entry.collectionId === item.collectionId &&
          (item.locationId
            ? entry.id === item.locationId
            : entry.name === item.location),
      );
      const locationId = location && recordKey(location);
      if (locationId) counts.set(locationId, (counts.get(locationId) || 0) + 1);
    });
    return counts;
  }, [items, locations, providedItemCounts]);

  const filteredLocations = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    return locations
      .filter(
        (location) =>
          location.name.toLocaleLowerCase().includes(query) ||
          location.description.toLocaleLowerCase().includes(query) ||
          Boolean(location.collectionName?.toLocaleLowerCase().includes(query)),
      )
      .sort((left, right) => {
        let result = 0;
        if (sort === 'items-desc' || sort === 'items-asc') {
          const leftKnown = itemCounts.has(recordKey(left));
          const rightKnown = itemCounts.has(recordKey(right));
          if (leftKnown !== rightKnown) return leftKnown ? -1 : 1;
        }
        switch (sort) {
          case 'name-asc':
            result = collator.compare(left.name, right.name);
            break;
          case 'name-desc':
            result = collator.compare(right.name, left.name);
            break;
          case 'edited-desc':
            result =
              Date.parse(right.lastEditedAt || right.createdAt) -
              Date.parse(left.lastEditedAt || left.createdAt);
            break;
          case 'created-desc':
            result = Date.parse(right.createdAt) - Date.parse(left.createdAt);
            break;
          case 'items-desc':
            result =
              (itemCounts.get(recordKey(right)) || 0) -
              (itemCounts.get(recordKey(left)) || 0);
            break;
          case 'items-asc':
            result =
              (itemCounts.get(recordKey(left)) || 0) -
              (itemCounts.get(recordKey(right)) || 0);
            break;
        }
        return (
          result ||
          collator.compare(left.name, right.name) ||
          collator.compare(left.id, right.id)
        );
      });
  }, [locations, searchQuery, sort, itemCounts]);
  useEffect(() => {
    onCountChange?.({
      result: filteredLocations.length,
      total: locations.length,
      incomplete: incompleteResults,
    });
  }, [
    onCountChange,
    filteredLocations.length,
    locations.length,
    incompleteResults,
  ]);

  const visibleIds = filteredLocations.map(recordKey);
  const emptyVisibleLocations = filteredLocations.filter(
    (location) => itemCounts.get(recordKey(location)) === 0,
  );

  const openEditor = (ids: string[]) => {
    const selected =
      ids.length === 1
        ? locations.find((location) => recordKey(location) === ids[0])
        : undefined;
    if (selected) {
      if (onOpenLocation) {
        onOpenLocation(selected);
        return;
      }
      setEditingLocation(selected);
      return;
    }
    setEditingIds(
      locations
        .filter((location) => ids.includes(recordKey(location)))
        .map((location) => location.id),
    );
    setEditDescription('');
    setShowEditDialog(true);
  };

  const saveEdits = () => {
    update(
      editingIds.map((id) => ({
        id,
        description: editDescription,
      })),
    );
    finishBulkEdit();
    if (selection.active) selection.clear();
  };

  const confirmDelete = () => {
    deleteLocations(
      locations
        .filter((location) => selection.selectedIds.has(recordKey(location)))
        .map((location) => location.id),
    );
    selection.clear();
    setShowDeleteConfirm(false);
  };

  const toolbar = (
    <CollectionSearchField
      label="Search locations"
      placeholder="Search locations…"
      clearLabel="Clear location search"
      value={searchQuery}
      onChange={setSearchQuery}
    />
  );

  const header = (
    <CollectionHeader
      hideCount={hideCount}
      onRefresh={onRefresh}
      incomplete={incompleteResults}
      inventoryStatus={inventoryStatus}
      noun="location"
      resultCount={filteredLocations.length}
      totalCount={locations.length}
      visibleIds={visibleIds}
      selection={selection}
      disableSelection={disableBulkActions}
      phoneLayout={phoneLayout}
      sortControl={
        <CollectionSortControl
          noun="location"
          value={sort}
          options={locationSortOptions}
          compact={phoneLayout}
          onChange={onSortChange}
        />
      }
      viewControl={
        <CollectionViewControl value={view} onChange={onViewChange} />
      }
      bulkActions={
        readOnly || disableBulkActions
          ? []
          : [
              {
                label: 'Edit descriptions',
                icon: <EditIcon fontSize="small" />,
                onClick: () => openEditor([...selection.selectedIds]),
                disabled: !selection.selectedIds.size,
              },
              {
                label: 'Delete locations',
                icon: <DeleteIcon fontSize="small" />,
                onClick: () => setShowDeleteConfirm(true),
                disabled: !selection.selectedIds.size,
                color: 'error',
              },
            ]
      }
      extraMenuActions={
        readOnly || disableBulkActions
          ? []
          : [
              {
                label: `Select empty results (${emptyVisibleLocations.length})`,
                icon: <FilterIcon fontSize="small" />,
                onClick: () =>
                  selection.select(emptyVisibleLocations.map(recordKey)),
                disabled: !emptyVisibleLocations.length,
              },
            ]
      }
    />
  );

  const results = filteredLocations.length ? (
    view === 'list' ? (
      <List
        disablePadding
        aria-label="Locations"
        sx={{ display: 'grid', gap: 1 }}
      >
        {filteredLocations.map((location) => (
          <LocationEntry
            key={recordKey(location)}
            location={location}
            itemCount={itemCounts.get(recordKey(location))}
            view="list"
            cardSize={cardSize}
            selection={selection}
            visibleIds={visibleIds}
            onOpen={() =>
              onOpenLocation
                ? onOpenLocation(location)
                : openEditor([recordKey(location)])
            }
            currentUserId={currentUserId}
            currentUsername={currentUsername}
            collectionId={collectionId}
            membershipVersion={membershipVersion}
          />
        ))}
      </List>
    ) : (
      <Box
        aria-label="Locations"
        data-testid="location-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${size.locationGridMinWidth}px), 1fr))`,
          gap: size.gridGap,
        }}
      >
        {filteredLocations.map((location) => (
          <LocationEntry
            key={recordKey(location)}
            location={location}
            itemCount={itemCounts.get(recordKey(location))}
            view="grid"
            cardSize={cardSize}
            selection={selection}
            visibleIds={visibleIds}
            onOpen={() =>
              onOpenLocation
                ? onOpenLocation(location)
                : openEditor([recordKey(location)])
            }
            currentUserId={currentUserId}
            currentUsername={currentUsername}
            collectionId={collectionId}
            membershipVersion={membershipVersion}
          />
        ))}
      </Box>
    )
  ) : null;

  return (
    <>
      <CollectionPage
        title="Locations"
        loading={loading}
        loadingIndicatorInHeader={Boolean(inventoryStatus)}
        error={error}
        fullWidth={removeMargins}
        stickyTop={stickyTop}
        shellHeader={shellHeader}
        toolbar={toolbar}
        header={header}
        empty={
          <CollectionEmptyState
            title={
              incompleteResults
                ? 'No matches in loaded results yet'
                : locations.length
                  ? 'No matching locations'
                  : 'No locations yet'
            }
            description={
              incompleteResults
                ? 'More results are available. Load more or search to check the remaining locations.'
                : locations.length
                  ? 'Try a different search.'
                  : 'Use the add button to create your first location.'
            }
            actionLabel={searchQuery ? 'Clear search' : undefined}
            onAction={searchQuery ? () => setSearchQuery('') : undefined}
          />
        }
        addLabel="Add location"
        onAdd={readOnly ? undefined : onAddLocation}
        phoneLayout={phoneLayout}
      >
        {results}
      </CollectionPage>

      <LocationEditorDialog
        scopeKey={editScopeKey}
        open={Boolean(editingLocation)}
        location={editingLocation}
        readOnly={readOnly}
        onClose={() => setEditingLocation(null)}
        onSave={controller.saveLocation}
        onDelete={controller.deleteLocation}
        currentUserId={currentUserId}
        currentUsername={currentUsername}
        collectionId={collectionId}
        membershipVersion={membershipVersion}
      />

      <ResponsiveDialog
        open={!readOnly && !disableBulkActions && showEditDialog}
        onClose={closeBulkEditWithGuard}
        aria-labelledby="edit-location-title"
      >
        <DialogHeader
          id="edit-location-title"
          title="Edit Descriptions"
          onClose={closeBulkEditWithGuard}
        />
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            fullWidth
            label="Description"
            value={editDescription}
            onChange={(event) => setEditDescription(event.target.value)}
            multiline
            helperText="This description will be applied to all selected locations"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkEditWithGuard}>Cancel</Button>
          <Button
            onClick={saveEdits}
            variant="contained"
            disabled={!editingIds.length}
          >
            Save Changes
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog
        open={!readOnly && !disableBulkActions && showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        aria-labelledby="delete-locations-title"
      >
        <DialogHeader
          id="delete-locations-title"
          title="Confirm Delete"
          onClose={() => setShowDeleteConfirm(false)}
        />
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selection.selectedIds.size}{' '}
            selected location{selection.selectedIds.size === 1 ? '' : 's'}? Some
            locations may contain items.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
